import React, { useState } from 'react';
import {ContactGuide} from '../components/FamilyGuide';
import { contactInfo } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Facebook, AlertCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  // Modification #31: Ajout des champs location, startDate, endDate
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    location: '',
    startDate: '',
    endDate: '',
    message: searchParams.get('materiel') ? `Demande de location : ${searchParams.get('materiel')}` : ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/devis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', service: '', location: '', startDate: '', endDate: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Nous <span className="text-green-400">Contacter</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à nous contacter pour obtenir un devis personnalisé.
          </p>
        </div>
      </section>

      {/* Contact Section - Modification #30: Formulaire à gauche, Infos à droite */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form - À GAUCHE (Modification #30) */}
            <div>
              <div className="bg-slate-50 rounded-3xl p-8 lg:p-10">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Demande de devis</h2>
                <p className="text-slate-500 mb-8">Remplissez le formulaire, nous vous répondons sous 24h</p>

                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="text-green-500" size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">Message envoyé !</h3>
                    <p className="text-slate-600">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                ) : (
                  <form data-testid="contact-quote-form" onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Nom & Prénom *
                        </label>
                        <input
                          type="text"
                          name="name"
                          data-testid="contact-name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          data-testid="contact-email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="jean@exemple.fr"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Téléphone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          data-testid="contact-phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Service concerné
                        </label>
                        <select
                          name="service"
                          data-testid="contact-service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="evenementiel">Evénementiel & Loisirs</option>
                          <option value="bricolage">Bricolage & Bâtiment</option>
                          <option value="jardin">Motoculture & Jardin</option>
                          <option value="nettoyage">Nettoyage & Logement</option>
                          <option value="autre">Autre demande</option>
                        </select>
                      </div>
                    </div>

                    {/* Modification #31: Nouveau champ Lieu */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Lieu de l'événement / du projet
                      </label>
                      <input
                        type="text"
                        name="location"
                        data-testid="contact-location"
                        value={formData.location}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Adresse ou ville (ex: Le Havre, Etretat...)"
                      />
                    </div>

                    {/* Modification #31: Nouveau champ Dates de location */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Date de début souhaitée
                        </label>
                        <input
                          type="date"
                          name="startDate"
                          data-testid="contact-start-date"
                          value={formData.startDate}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Date de fin souhaitée
                        </label>
                        <input
                          type="date"
                          name="endDate"
                          data-testid="contact-end-date"
                          value={formData.endDate}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Votre message *
                      </label>
                      <textarea
                        name="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        placeholder="Décrivez votre projet, le matériel recherché..."
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer le message
                          <Send size={20} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info - À DROITE (Modification #30) */}
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-8">Informations de contact</h2>
              
              <div className="space-y-6 mb-10">
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors">
                    <Phone className="text-green-600 group-hover:text-white transition-colors" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Téléphone</p>
                    <p className="text-lg font-semibold text-slate-800">{contactInfo.phone}</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors">
                    <Mail className="text-green-600 group-hover:text-white transition-colors" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="text-lg font-semibold text-slate-800">{contactInfo.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                    <MapPin className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Adresse</p>
                    <p className="text-lg font-semibold text-slate-800">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-2xl">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Horaires d'ouverture</p>
                    <p className="font-semibold text-slate-800">{contactInfo.hours.weekdays}</p>
                    <p className="font-semibold text-slate-800">{contactInfo.hours.saturday}</p>
                    <p className="font-semibold text-slate-800">{contactInfo.hours.sunday}</p>
                    <p className="text-green-600 text-sm mt-1 font-medium">{contactInfo.hours.note}</p>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <p className="text-sm text-slate-500 mb-4">Suivez-nous sur les réseaux</p>
                <div className="flex gap-4">
                  <a 
                    href="https://www.facebook.com/3dlocation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center hover:bg-green-500 hover:text-white text-slate-600 transition-colors"
                  >
                    <Facebook size={24} />
                  </a>
                </div>
              </div>

              {/* Google Maps - 3DLoc Location */}
              <div className="mt-10">
                <div className="rounded-2xl overflow-hidden h-64">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2595.5!2d0.1167!3d49.5833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e025c1ed27e9d9%3A0x7e06f2252871d58e!2sLocation%20et%20Projets%20(3DLoc)!5e0!3m2!1sfr!2sfr!4v1710000000000!5m2!1sfr!2sfr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localisation 3DLoc - Cauville-sur-Mer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ContactGuide />
    </div>
  );
};

export default ContactPage;
